import React, { useState, useEffect, useMemo, useRef, useCallback } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import "../../theme/redesign/tokens.css";
import "../../theme/redesign/shell.css";
import "../../theme/redesign/section.css";
import "../../theme/redesign/offer.css";

const PATH_TO_BACKEND = process.env.REACT_APP_PATH_TO_BACKEND;
const PAGE_SIZE = 100;

const toastOptions = {
    position: "top-right",
    autoClose: 5000,
    hideProgressBar: false,
    closeOnClick: true,
    pauseOnHover: true,
    draggable: true,
    theme: "light",
};

const normalizeText = (text) => (text ? text.toLowerCase().trim() : "");

const formatPrice = (price) => {
    const numericPrice = Number(price);
    if (price === null || price === undefined || isNaN(numericPrice)) return "-";
    return numericPrice.toLocaleString("ru-RU", { maximumFractionDigits: 2 });
};

// Остатки приходят из последней выгрузки StockItems (numeric(…,3)); null — позиции
// в выгрузке нет. Дробные показываем как есть, целые — без хвоста ",000".
const formatQuantity = (quantity) => {
    const numeric = Number(quantity);
    if (quantity === null || quantity === undefined || isNaN(numeric)) return "—";
    return numeric.toLocaleString("ru-RU", { maximumFractionDigits: 3 });
};

const WarehouseMurinoPrices = () => {
    const [data, setData] = useState([]);
    // Метка последней выгрузки остатков ({ snapshot_id, at }) — null, если выгрузки нет.
    const [feed, setFeed] = useState(null);
    const [loading, setLoading] = useState(true);
    const [inputValue, setInputValue] = useState("");
    const [searchValue, setSearchValue] = useState("");
    const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);
    // Результат умного поиска (search-service): список id в порядке релевантности.
    // null — умный поиск не использовался/недоступен, тогда filteredData откатывается
    // на локальный .includes()-фильтр.
    const [smartSearchIds, setSmartSearchIds] = useState(null);
    // Пока запрос к search-service летит по сети — НЕ переключаем таблицу на
    // промежуточный локальный фильтр (иначе список на секунду "схлопывается" на
    // грубых .includes()-результатах, а потом подменяется умными — заметный скачок).
    // Вместо этого держим на экране предыдущий результат до окончательного ответа.
    const [isSmartSearching, setIsSmartSearching] = useState(false);
    const searchInputRef = useRef(null);
    const debounceTimeoutRef = useRef(null);
    const searchRequestIdRef = useRef(0);

    useEffect(() => {
        const fetchData = async () => {
            setLoading(true);
            try {
                const response = await axios.get(`${PATH_TO_BACKEND}/api/adminka/warehouse_stock_murino`);
                if (response.data && response.data.success && Array.isArray(response.data.data)) {
                    setData(response.data.data);
                    setFeed(response.data.feed || null);
                } else {
                    throw new Error("Не удалось загрузить цены склада Мурино");
                }
            } catch (error) {
                console.error("Ошибка при загрузке цен склада Мурино:", error);
                toast.error(`Ошибка при загрузке цен склада Мурино: ${error.message}`, toastOptions);
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, []);

    useEffect(() => {
        if (searchInputRef.current) {
            searchInputRef.current.focus();
        }
    }, [loading]);

    useEffect(() => {
        return () => {
            if (debounceTimeoutRef.current) {
                clearTimeout(debounceTimeoutRef.current);
            }
        };
    }, []);

    const debouncedSetSearchValue = useCallback((value) => {
        if (debounceTimeoutRef.current) {
            clearTimeout(debounceTimeoutRef.current);
        }
        debounceTimeoutRef.current = setTimeout(() => {
            setSearchValue(value);
        }, 500);
    }, []);

    const handleSearchChange = (e) => {
        const newValue = e.target.value;
        setInputValue(newValue);
        setVisibleCount(PAGE_SIZE);
        if (!newValue.trim()) {
            setSearchValue("");
        } else {
            debouncedSetSearchValue(newValue);
        }
    };

    const handleClearSearch = () => {
        setInputValue("");
        setSearchValue("");
        setVisibleCount(PAGE_SIZE);
        setSmartSearchIds(null);
    };

    const localFilter = useCallback((rawQuery) => {
        const query = normalizeText(rawQuery.trim());
        if (!query) return data;
        return data.filter((item) => {
            const nomenclature = normalizeText(item.nomenclature);
            const characteristic = normalizeText(item.characteristic);
            return nomenclature.includes(query) || characteristic.includes(query);
        });
    }, [data]);

    // Итоговый список строк для показа. Обновляется ЦЕЛИКОМ и ОДИН РАЗ на каждый
    // осевший поиск — пока запрос к search-service летит по сети, здесь остаётся
    // предыдущий результат (без промежуточного локального фильтра), чтобы таблица
    // не "прыгала" визуально между грубым и умным результатом.
    const [filteredData, setFilteredData] = useState(data);

    // Умный поиск через search-service (синонимы/опечатки/раскладка/pg_trgm), проксируемый
    // бэкендом. Если бэкенд недоступен/вернул searchServiceUnavailable — тихий откат
    // на локальный .includes()-фильтр, но только когда ответ (успешный или неуспешный)
    // реально пришёл — не раньше.
    useEffect(() => {
        const query = searchValue.trim();
        if (!query) {
            setSmartSearchIds(null);
            setIsSmartSearching(false);
            setFilteredData(data);
            return;
        }

        const requestId = ++searchRequestIdRef.current;
        setIsSmartSearching(true);

        const runSmartSearch = async () => {
            try {
                const response = await axios.post(`${PATH_TO_BACKEND}/api/adminka/warehouse_murino/search-ids`, {
                    q: query,
                });
                if (requestId !== searchRequestIdRef.current) return; // устарел, пришёл ответ на более старый запрос

                if (
                    response.data &&
                    response.data.success &&
                    !response.data.searchServiceUnavailable &&
                    Array.isArray(response.data.data) &&
                    response.data.data.length > 0
                ) {
                    const ids = response.data.data;
                    setSmartSearchIds(ids);
                    const scoreById = new Map(ids.map(({ id, score }) => [id, score]));
                    setFilteredData(
                        data
                            .filter((item) => scoreById.has(item.id))
                            .sort((a, b) => scoreById.get(b.id) - scoreById.get(a.id))
                    );
                } else {
                    setSmartSearchIds(null); // откат на локальный фильтр
                    setFilteredData(localFilter(query));
                }
            } catch (error) {
                if (requestId !== searchRequestIdRef.current) return;
                console.warn("Умный поиск недоступен, используется локальный фильтр:", error.message);
                setSmartSearchIds(null);
                setFilteredData(localFilter(query));
            } finally {
                if (requestId === searchRequestIdRef.current) {
                    setIsSmartSearching(false);
                }
            }
        };

        runSmartSearch();
    }, [searchValue, data, localFilter]);

    const displayedData = useMemo(() => filteredData.slice(0, visibleCount), [filteredData, visibleCount]);

    const handleShowMore = () => {
        setVisibleCount((prev) => prev + PAGE_SIZE);
    };

    return (
        <div className="app-redesign" style={{ minHeight: "100vh" }}>
            <div className="rd-shell">
                <div className="rd-section">
                    <div className="rd-sec-head">
                        <h2>Склад Мурино — цены и остатки</h2>
                        <Link to="/" className="rd-scenario-btn rd-scenario-btn--neutral">Вернуться на главную</Link>
                    </div>
                    {!loading && data.length > 0 && (
                        <div style={{ fontSize: "13px", color: "var(--rd-dim)", marginBottom: "12px" }}>
                            {feed && feed.at
                                ? `Остатки по выгрузке из 1С от ${feed.at}`
                                : "Выгрузки остатков из 1С нет — остатки не показаны"}
                        </div>
                    )}

                    {loading ? (
                        <div>Загрузка данных...</div>
                    ) : data.length === 0 ? (
                        <div>Данные по складу Мурино отсутствуют.</div>
                    ) : (
                        <>
                            <div style={{ display: "flex", alignItems: "center", gap: "10px", marginBottom: "16px" }}>
                                <input
                                    type="text"
                                    placeholder="Поиск по номенклатуре или характеристике..."
                                    value={inputValue}
                                    onChange={handleSearchChange}
                                    ref={searchInputRef}
                                    style={{
                                        flex: "0 1 360px",
                                        padding: "8px 12px",
                                        border: "1px solid rgba(2, 2, 23, 0.12)",
                                        borderRadius: "8px",
                                        fontFamily: "'Inter', sans-serif",
                                        fontSize: "14px",
                                        color: "var(--rd-ink)",
                                    }}
                                />
                                {inputValue && (
                                    <button onClick={handleClearSearch} className="rd-scenario-btn rd-scenario-btn--neutral" style={{ padding: "8px 16px", fontSize: "13px" }}>
                                        Очистить
                                    </button>
                                )}
                                {isSmartSearching && (
                                    <span style={{ fontSize: "13px", color: "var(--rd-dim)" }}>Поиск...</span>
                                )}
                            </div>

                            {filteredData.length === 0 ? (
                                <div>По вашему запросу ничего не найдено.</div>
                            ) : (
                                <>
                                    <div style={{ overflowX: "auto" }}>
                                        <table className="rd-kp-table">
                                            <thead>
                                                <tr>
                                                    <th>№</th>
                                                    <th className="rd-kp-th--text">Номенклатура</th>
                                                    <th>Ед. изм.</th>
                                                    <th title="Остаток на складе по последней выгрузке из 1С">Остаток</th>
                                                    <th title="Зарезервировано под заказы">Резерв</th>
                                                    <th title="Остаток минус резерв — доступно к продаже">Свободно</th>
                                                    <th title="Едет на склад">В пути</th>
                                                    <th>Цена (нал)</th>
                                                    <th>Цена (безнал)</th>
                                                    <th>Дата обновления</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {displayedData.map((item, index) => (
                                                    <tr key={item.id}>
                                                        <td>{index + 1}</td>
                                                        <td className="rd-kp-td--text">
                                                            {item.characteristic
                                                                ? `${item.nomenclature} ${item.characteristic}`
                                                                : item.nomenclature || "-"}
                                                            {item.in_last_feed === false && (
                                                                <span
                                                                    style={{ marginLeft: "8px", fontSize: "12px", color: "var(--rd-dim)" }}
                                                                    title="Позиции нет в последней выгрузке остатков из 1С"
                                                                >
                                                                    нет в выгрузке
                                                                </span>
                                                            )}
                                                        </td>
                                                        <td className="unit-cell">{item.unit || "-"}</td>
                                                        <td className="money-cell">{formatQuantity(item.stock_remains_quantity)}</td>
                                                        <td className="money-cell">{formatQuantity(item.reserved_stock_quantity)}</td>
                                                        <td className="money-cell">{formatQuantity(item.free_quantity)}</td>
                                                        <td className="money-cell">{formatQuantity(item.in_transit_quantity)}</td>
                                                        <td className="money-cell">{formatPrice(item.price_cash)}</td>
                                                        <td className="money-cell">{formatPrice(item.price_non_cash)}</td>
                                                        <td>{item.price_updated_at || "-"}</td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                    {visibleCount < filteredData.length && (
                                        <button
                                            onClick={handleShowMore}
                                            className="rd-scenario-btn rd-scenario-btn--accent"
                                            style={{ marginTop: "14px" }}
                                        >
                                            Показать ещё
                                        </button>
                                    )}
                                </>
                            )}
                        </>
                    )}
                </div>
            </div>
        </div>
    );
};

export default WarehouseMurinoPrices;
